import React, { createContext, useContext, useEffect, useState } from 'react';

import { getFiltersConfig } from 'src/components/price-list/filters-config';
import { TranslateFunc } from 'src/components/translate';

import {
  FilterScope,
  trackFilterSelectEvent,
} from 'src/globals/tracking/filter';
import { useAppContext } from 'src/components/app-context';
import { trackPriceListFilterUpdate } from '../tracking';

import { getDeliveryMethodsConfig } from '../price-list/offer-list-filters/shipping-filters/delivery-methods/utils';

import {
  FilterSelection,
  FilterType,
  FilterUIPlacementEnum,
  IFilter,
  PriceDisplayEnum,
} from 'src/components/price-list/types';
import { IOfferListFilterContext, ContextProps } from './types';

const initialContextValue: IOfferListFilterContext = {
  getActiveFilters: () => [],
  getAdditiveFilters: () => [],
  getFilter: () => undefined,
  getTranslatedLabel: () => '',
  getFilterByPlacement: () => [],
  getPriceDisplay: () => PriceDisplayEnum.PRICE,
  toggleFilter: () => {},
  updateMultipleFilters: () => undefined,
  setFilterSelection: () => {},
  resetFilters: () => {},
  resetFiltersByPlacement: () => {},
  filtersToggledOnByUser: () => [],
};

const OfferListFilterContext = createContext<IOfferListFilterContext>(
  initialContextValue
);

export const useOfferListFilterContext = () =>
  useContext(OfferListFilterContext);

export const OfferListFilterContextContainer = ({ children }: ContextProps) => {
  const { market } = useAppContext();
  const [filters, setFilters] = useState<IFilter[]>(getFiltersConfig());

  useEffect(() => {
    initFilters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getActiveFilters = () => {
    return filters.filter(filter => filter.active);
  };

  const getAdditiveFilters = () => {
    return filters.filter(filter => filter.additive);
  };

  const getFilter = (filterType: FilterType) => {
    return filters.find(filter => filter.type === filterType);
  };

  const getFilterByPlacement = (placement: FilterUIPlacementEnum) => {
    return filters.filter(filter => filter.placement === placement);
  };

  const getPriceDisplay = () => {
    return getFilter(FilterType.PRICES_INC_SHIPPING)?.active
      ? PriceDisplayEnum.PRICE_INCLUDES_SHIPPING
      : PriceDisplayEnum.PRICE;
  };

  const getTranslatedLabel = (t: TranslateFunc, type: FilterType) => {
    const filter = getFilter(type);
    const hasSelections = filter?.selection && filter?.selection.length > 0;

    let prefix: string;
    switch (filter?.placement) {
      case FilterUIPlacementEnum.OFFERLIST_HEADER:
        prefix = 'common';
        break;
      default:
        prefix = 'product';
        break;
    }

    let key;
    if (filter?.label) {
      key = filter?.label;
    }
    if (filter?.type === FilterType.DELIVERY_METHOD && hasSelections) {
      const deliveryOption = getDeliveryMethodsConfig().find(
        option => option.type === filter!.selection![0]
      );
      key = deliveryOption?.translationString;
    }

    if (key) {
      // @ts-ignore
      return t(prefix, key) as string;
    }
    if (hasSelections) {
      return filter.selection!.join(', ');
    }
    return '';
  };

  const setDefaultHiddenFilters = (
    filtersList: IFilter[],
    type: FilterType[]
  ) => {
    return filtersList.map(filter => {
      const updatedFilter = { ...filter };
      if (!type.includes(filter.type)) {
        updatedFilter.hidden = true;
      }

      return updatedFilter;
    });
  };

  const updateMultipleFilters = (newFilters: IFilter[]) => {
    const updatedFilters = filters.map(filter => {
      let replacedFilter = filter;
      newFilters.forEach(newFilter => {
        if (newFilter.type === filter.type) {
          replacedFilter = newFilter;
        }
      });
      return replacedFilter;
    });
    setFilters(updatedFilters);
  };

  const setFilterSelection = (
    filterType: FilterType,
    selection: FilterSelection
  ) => {
    const updatedFilters = filters.map(filter => {
      if (filterType === filter.type) {
        return { ...filter, selection, active: Boolean(selection?.length) };
      }
      return filter;
    });
    setFilters(updatedFilters);
    trackPriceListFilterUpdate(filterType, true);
  };

  const toggleFilter = (filterType: FilterType) => {
    const filter = filters.find(f => f.type === filterType);
    const newVal = !filter?.active;
    const trackingVal = filter?.additive ? !newVal : newVal;
    const updatedFilters = filters.map(f => {
      if (filterType === f.type) {
        return { ...f, active: newVal };
      }
      return f;
    });
    setFilters(updatedFilters);
    trackPriceListFilterUpdate(filterType, trackingVal);
    trackFilterSelectEvent({
      value: trackingVal.toString(),
      scope: FilterScope.PRODUCT,
      property: filterType,
      layout: 'quick-filter',
      type: 'boolean',
      category: null,
    });
  };

  const resetFiltersByPlacement = (filterPlacement: FilterUIPlacementEnum) => {
    const updatedFilters = filters.map(filter => {
      if (filter.placement === filterPlacement) {
        let activeState = false;
        if (filter.additive) {
          activeState = true;
        }
        return {
          ...filter,
          active: activeState,
        };
      }
      return filter;
    });
    setFilters(updatedFilters);
  };

  const initFilters = () => {
    const initialFilters = getFiltersConfig();
    const hiddenFilters = market.offerList?.filters?.hiddenFilters ?? [];
    const updatedFilters = setDefaultHiddenFilters(
      initialFilters,
      hiddenFilters
    );

    const searchParams: URLSearchParams = new URLSearchParams(
      window.location.search
    );
    const filtersFromQuery: any[] =
      searchParams.get('filters')?.split(',') || [];

    const filtersFromQueryString = updatedFilters.map(filter => {
      const isInQueryString = filtersFromQuery.includes(filter.type);
      return { ...filter, active: isInQueryString || filter.active };
    });

    setFilters(filtersFromQueryString);
  };

  const resetFilters = () => {
    const initialFilters = getFiltersConfig();
    const hiddenFilters = market.offerList?.filters?.hiddenFilters ?? [];
    const updatedFilters = setDefaultHiddenFilters(
      initialFilters,
      hiddenFilters
    );
    setFilters(updatedFilters);
  };

  const filtersToggledOnByUser = () => {
    const appliedNormalFilters = filters
      .filter(f => !f.additive && f.active)
      .filter(
        f =>
          !f.selection ||
          f.selection?.toString() !== f.initialSelection?.toString()
      );
    const appliedAdditiveFilters = getAdditiveFilters().filter(
      f => f.additive && !f.active
    );
    return [...appliedNormalFilters, ...appliedAdditiveFilters];
  };

  return (
    <OfferListFilterContext.Provider
      value={{
        getActiveFilters,
        getAdditiveFilters,
        getFilter,
        getTranslatedLabel,
        getFilterByPlacement,
        getPriceDisplay,
        toggleFilter,
        updateMultipleFilters,
        setFilterSelection,
        resetFilters,
        resetFiltersByPlacement,
        filtersToggledOnByUser,
      }}
    >
      {children}
    </OfferListFilterContext.Provider>
  );
};
